/**
 * Build labels in the management UI: which build of a publish run a job is.
 *
 * One pure-publish run submits several compile jobs and runs two local
 * training runs between them; they used to read as one undifferentiated
 * "addon build". The VOCABULARY is owned by internal/publish
 * (src/compile/buildLabels.ts) and documented in
 * internal/compiler/plans/2026-10-13-build-labels.md; the submitter sends
 * each job's kind + display label + position in the run (stored on the api
 * job row, migration 0015) and the pipeline events carry the whole ordered
 * list (D1 `publish_versions.compile_jobs`, private/auth migration 0081).
 * The pages show those words verbatim. This file only
 *   - falls back to the SAME kind derived from fields the job row already has
 *     (older rows, and the kinds those fields decide on their own),
 *   - formats sequence ("3/5"), platform/libc and durations,
 *   - merges the recorded list with the live job rows (status, duration).
 *
 * The kind → label table below is pinned by buildLabels.test.ts against the
 * same table internal/publish's test pins: change one, change the other.
 */

export const BUILD_KIND_LABELS: Record<string, string> = {
  "package-test": "package-test build",
  "full-project": "full-project build",
  "test-build": "package-test + full-project build",
  "type-profile": "type-profile collection",
  "type-profile-training": "type-profile training run",
  "llvm-instrumented": "LLVM instrumented",
  "llvm-training": "LLVM training run",
  "final-pgo": "final (PGO)",
  final: "final (no LLVM PGO)",
  "plain-rebuild": "plain rebuild (PGO fallback)",
  "darwin-followup": "darwin arm64 follow-up",
  "bolt-training": "BOLT training run",
  "final-bolt": "final (BOLT)",
};

/** What a job row (admin jobs list / detail) says about its build. */
export interface JobBuildFields {
  build_kind?: string | null;
  build_label?: string | null;
  build_seq?: number | null;
  build_run?: string | null;
  type_profile_collect?: number | null;
  pgo_generate?: number | null;
  pgo_profile_path?: string | null;
  bolt_profile_path?: string | null;
  platform?: string | null;
  libc?: string | null;
  glibc_version?: string | null;
  target?: string | null;
}

/** The kind the job's own columns decide (same precedence as publish's
 *  deriveBuildKind), or null when they do not. */
export function deriveBuildKind(f: JobBuildFields): string | null {
  if (f.platform === "macos-arm64" || f.platform === "macos-x64")
    return "darwin-followup";
  if (f.type_profile_collect) return "type-profile";
  if (f.pgo_generate) return "llvm-instrumented";
  if (f.bolt_profile_path) return "final-bolt";
  if (f.pgo_profile_path) return "final-pgo";
  return null;
}

export interface BuildKindInfo {
  kind: string;
  label: string;
  /** true = computed here from the job's fields; false = named by the
   *  submitter (stored on the row). */
  derived: boolean;
}

/** A job's build kind + the words to show: the submitter's own when stored,
 *  else derived from the row's fields; null for an ordinary (customer) job. */
export function buildKindOf(f: JobBuildFields): BuildKindInfo | null {
  if (f.build_kind) {
    return {
      kind: f.build_kind,
      label: f.build_label || BUILD_KIND_LABELS[f.build_kind] || f.build_kind,
      derived: false,
    };
  }
  const kind = deriveBuildKind(f);
  return kind
    ? { kind, label: BUILD_KIND_LABELS[kind] ?? kind, derived: true }
    : null;
}

/** "linux-x64 · musl", "linux-x64 · glibc 2.28", "macos-arm64". The platform
 *  and libc a job was submitted with (the API defaults: linux-x64, musl). A
 *  linux node-addon is one libc-agnostic musl-built job, so it reads "musl". */
export function platformLabel(f: JobBuildFields): string {
  const platform = f.platform || "linux-x64";
  if (platform.startsWith("macos")) return platform;
  const libc = f.libc === "glibc" ? "glibc" : "musl";
  return libc === "glibc" && f.glibc_version
    ? `${platform} · glibc ${f.glibc_version}`
    : `${platform} · ${libc}`;
}

/** "3/5" for build 3 of the 5 builds known for its run; "3" while the run's
 *  siblings are not known; null when the job has no sequence number. */
export function sequenceText(
  seq: number | null | undefined,
  total?: number,
): string | null {
  if (!seq) return null;
  return total && total >= seq ? `${seq}/${total}` : String(seq);
}

/** "2m 03s", "41s", "1h 05m"; null when there is no start. */
export function fmtDurationMs(
  startMs: number | null | undefined,
  endMs: number | null | undefined,
  nowMs: number = Date.now(),
): string | null {
  if (!startMs) return null;
  const total = Math.max(0, Math.round(((endMs ?? nowMs) - startMs) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// ── The recorded list of a publish attempt (publish_versions.compile_jobs) ──

export type BuildStatus = "running" | "succeeded" | "failed" | "cancelled";

/** Mirrors internal/publish compile/buildLabels.ts CompileJobRecord. */
export interface CompileJobRecord {
  seq: number;
  kind: string;
  label: string;
  jobId?: string;
  platform?: string;
  local?: true;
  status: BuildStatus;
  startedAt: number;
  endedAt?: number;
}

/** `compile_jobs` JSON (or the already-parsed `compileJobs` of a run
 *  history entry) → records, in sequence order; [] when unset/unreadable. */
export function parseCompileJobs(raw: unknown): CompileJobRecord[] {
  let v: unknown = raw;
  if (typeof raw === "string") {
    try {
      v = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(v)) return [];
  const out: CompileJobRecord[] = [];
  for (const e of v) {
    if (!e || typeof e !== "object") continue;
    const r = e as Record<string, unknown>;
    if (typeof r.kind !== "string" || typeof r.seq !== "number") continue;
    out.push({
      seq: r.seq,
      kind: r.kind,
      label:
        typeof r.label === "string" && r.label
          ? r.label
          : (BUILD_KIND_LABELS[r.kind] ?? r.kind),
      ...(typeof r.jobId === "string" && r.jobId ? { jobId: r.jobId } : {}),
      ...(typeof r.platform === "string" ? { platform: r.platform } : {}),
      ...(r.local === true ? { local: true as const } : {}),
      status:
        r.status === "succeeded" ||
        r.status === "failed" ||
        r.status === "cancelled"
          ? r.status
          : "running",
      startedAt: typeof r.startedAt === "number" ? r.startedAt : 0,
      ...(typeof r.endedAt === "number" ? { endedAt: r.endedAt } : {}),
    });
  }
  return out.sort((a, b) => a.seq - b.seq);
}

/** What the admin jobs list says about one job (a subset of its row). */
export interface LiveJob {
  /** Guest memory summary columns (internal/api migration 0017); absent/null
   *  when the job's runner predates the sampler. */
  mem_peak_mb?: number | null;
  mem_peak_process?: string | null;
  mem_peak_process_mb?: number | null;
  mem_guest_mb?: number | null;
  mem_complete?: number | null;
  /** Warm-cache summary (internal/api migration 0019; lib/cacheVerdict.ts). */
  cold_unit_count?: number | null;
  unexpected_cold_count?: number | null;
  cold_top?: string | null;
  build_kind?: string | null;
  pgo_generate?: number | null;
  pgo_profile_path?: string | null;
  id: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  /** The failure verdict (internal/api migration 0018; lib/failureClass.ts). */
  failure_class?: string | null;
  failure_cause?: string | null;
  bundle_key?: string | null;
  failure_backfilled?: boolean;
}

export interface BuildRow {
  /** Position in the run; null for the decoupled darwin follow-up. */
  seq: number | null;
  /** "3/5" (null for the darwin follow-up). */
  seqText: string | null;
  kind: string;
  label: string;
  platform: string | null;
  jobId: string | null;
  /** A local training run on the publish host (no compile job). */
  local: boolean;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  /** Compile time for a job (its own start → finish on a runner), else the
   *  recorded span of a local run; null while still queued. */
  duration: string | null;
  /** Failure verdict of the live job row (class chip + cause + bundle flag);
   *  absent for a build whose job is unknown or did not fail. */
  failure?: {
    failure_class: string | null;
    failure_cause: string | null;
    bundle_key: string | null;
    failure_backfilled: boolean;
  };
}

/** The failure fields a build row carries from its live job row (no key at
 *  all when the job has no verdict, so rows of healthy builds are unchanged). */
function failureField(job: LiveJob | undefined): Pick<BuildRow, "failure"> {
  if (!job || !job.failure_class) return {};
  return {
    failure: {
      failure_class: job.failure_class,
      failure_cause: job.failure_cause ?? null,
      bundle_key: job.bundle_key ?? null,
      failure_backfilled: job.failure_backfilled === true,
    },
  };
}

/** The attempt's builds as the Registry page lists them: the recorded list
 *  (kind, label, seq) with the live job row's status and duration where the
 *  job is known, plus the darwin follow-up's job when native_targets names
 *  one the list does not. */
export function buildRows(
  records: CompileJobRecord[],
  live: ReadonlyMap<string, LiveJob>,
  darwinJobId: string | null = null,
  nowMs: number = Date.now(),
): BuildRow[] {
  const total = records.length;
  const rows: BuildRow[] = records.map((r) => {
    const job = r.jobId ? live.get(r.jobId) : undefined;
    let status: BuildRow["status"] = r.status;
    let duration: string | null;
    if (job) {
      status = job.status;
      duration =
        job.status === "queued"
          ? null
          : fmtDurationMs(
              job.started_at ?? job.created_at,
              job.finished_at,
              nowMs,
            );
    } else {
      duration = fmtDurationMs(
        r.startedAt,
        r.endedAt ?? (r.status === "running" ? undefined : r.startedAt),
        nowMs,
      );
    }
    return {
      seq: r.seq,
      seqText: sequenceText(r.seq, total),
      kind: r.kind,
      label: r.label,
      platform: r.platform ?? null,
      jobId: r.jobId ?? null,
      local: r.local === true,
      status,
      duration,
      ...failureField(job),
    };
  });
  if (darwinJobId && !rows.some((r) => r.jobId === darwinJobId)) {
    const job = live.get(darwinJobId);
    rows.push({
      seq: null,
      seqText: null,
      kind: "darwin-followup",
      label: BUILD_KIND_LABELS["darwin-followup"]!,
      platform: "macos-arm64",
      jobId: darwinJobId,
      local: false,
      status: job?.status ?? "queued",
      duration: job
        ? job.status === "queued"
          ? null
          : fmtDurationMs(
              job.started_at ?? job.created_at,
              job.finished_at,
              nowMs,
            )
        : null,
      ...failureField(job),
    });
  }
  return rows;
}
